import type { AccountOption } from "@app/components/account-combobox";
import type { StakeholderOption } from "@app/components/stakeholder-combobox";
import TxnTablePanel, {
  TXN_PANEL_DIALOG_CLASS,
  useTxnSort,
} from "@app/components/txn-table-panel";
import { useTxnBulkActions } from "@app/components/txn-table-panel/use-txn-bulk-actions";
import { useTxnPanelRefresh } from "@app/components/txn-table-panel/use-txn-panel-refresh";
import { getPerspectiveId } from "@app/lib/perspective";
import * as Dialog from "@radix-ui/react-dialog";
import { Suspense, useState } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";

import type { stakeholderTxnsPanel_Query } from "@app/queries/stakeholderTxnsPanel_Query.graphql";

const panelQuery = graphql`
  query stakeholderTxnsPanel_Query(
    $counterpartyId: ID
    $perspectiveId: ID
    $sort: TxnSortEnum
    $sortDirection: String
  ) {
    transactions(
      counterpartyId: $counterpartyId
      perspectiveId: $perspectiveId
      sort: $sort
      sortDirection: $sortDirection
    ) {
      totalCount
      totalAmount
      limit
      txns {
        txn {
          id
        }
        md5
        postingDate
        description
        amount
        kind
        accountNumber
        internal
        classification
        counterparty {
          name
        }
        category
        expenseNature {
          id
          name
        }
      }
    }
    allStakeholders {
      id
      name
    }
    bankAccounts {
      id
      name
      nickname
      last4
    }
    expenseNatures(perspectiveId: $perspectiveId) {
      id
      name
    }
  }
`;

// The plumbing nodes (Root + per-account BASE) are never valid counterparties.
const isRealStakeholder = (s: { name: string }) =>
  !s.name.startsWith("&") &&
  !s.name.toLowerCase().includes("root stakeholder") &&
  s.name !== "_root";

// The counterparty drill: a stakeholder's transactions on the shared editable shell,
// with the full bulk-action set from the shared registry (set counterparty, create
// rule, mark internal, reclassify, tag nature). Everything re-derives server-side,
// so onChanged() refreshes the merge page's totals/counts afterwards.
export default function StakeholderTxnsPanel({
  stakeholder,
  onClose,
  onChanged,
}: {
  stakeholder: { id: string; name: string };
  onClose: () => void;
  onChanged: () => void;
}) {
  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/20 z-40" />
        <Dialog.Content className={TXN_PANEL_DIALOG_CLASS}>
          <Suspense fallback={<div className="p-6 text-sm text-gray-400">Loading transactions…</div>}>
            <PanelContent stakeholder={stakeholder} onClose={onClose} onChanged={onChanged} />
          </Suspense>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function PanelContent({
  stakeholder,
  onClose,
  onChanged,
}: {
  stakeholder: { id: string; name: string };
  onClose: () => void;
  onChanged: () => void;
}) {
  const { refreshing, refresh, queryOptions } = useTxnPanelRefresh();
  const { sort, direction, onSort, sorting, sortVars } = useTxnSort();

  const data = useLazyLoadQuery<stakeholderTxnsPanel_Query>(
    panelQuery,
    { counterpartyId: stakeholder.id, perspectiveId: getPerspectiveId() ?? undefined, ...sortVars },
    queryOptions
  );
  const rows = data.transactions.txns;
  const stakeholderOptions: StakeholderOption[] = data.allStakeholders.filter(isRealStakeholder);
  const accountOptions: AccountOption[] = data.bankAccounts.map((a) => ({
    id: a.id,
    name: a.name,
    nickname: a.nickname,
    last4: a.last4,
  }));

  const { busy, renderBulkActions, dialogs } = useTxnBulkActions({
    enabled: ["setCounterparty", "createRule", "markInternal", "reclassify", "tagNature"],
    perspectiveId: getPerspectiveId(),
    onChanged: () => {
      refresh();
      onChanged();
    },
    rows,
    stakeholderOptions,
    accountOptions,
    natureOptions: data.expenseNatures,
  });

  const exportParams: Record<string, string> = {
    counterparty_id: stakeholder.id,
    sort: sortVars.sort,
    sort_direction: sortVars.sortDirection,
  };
  const perspectiveId = getPerspectiveId();
  if (perspectiveId) exportParams.perspective_id = perspectiveId;

  return (
    <>
      <TxnTablePanel
        title={stakeholder.name}
        totalCount={data.transactions.totalCount}
        headerTotalDollars={data.transactions.totalAmount}
        rows={rows}
        emptyLabel="No transactions for this counterparty."
        sort={sort}
        direction={direction}
        onSort={onSort} sorting={sorting}
        exportParams={exportParams}
        busy={busy || refreshing}
        onClose={onClose}
        renderBulkActions={renderBulkActions}
      />
      {dialogs}
    </>
  );
}
